import { closestCenter, DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { Column } from "@tanstack/react-table";
import { useRef, useState } from "react";

import type { DispatchGrain } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { DispatchTableRow } from "@/hooks/dispatch/dispatchTableRow";
import type { DataTableFeatures, DataTableInstance } from "@/ui/dataTableFeatures";
import { zinc } from "@/ui/tokens";
import { isStructuralColumnId } from "./columns";
import DispatchFieldPicker, { type FieldOption } from "./DispatchFieldPicker";
import { headerButtonSx } from "./DispatchFilterMenu";
import { denseSelectSx } from "./denseSelect";

export type { FieldOption } from "./DispatchFieldPicker";

/** The sort in force, as the Display popover edits it. */
export type DisplaySort = { key: string; direction: "asc" | "desc" } | null;

type ColumnRow = { id: string; label: string; visible: boolean; pinned: boolean; fromView: boolean };

const selectSx = { ...denseSelectSx(30), minWidth: 0, flex: 1 } as const;

const menuSlotProps = {
  paper: { sx: { bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}`, maxHeight: 320 } },
} as const;

const sectionLabelSx = {
  px: 0.5,
  py: 0.5,
  fontSize: "0.6875rem",
  fontWeight: 600,
  color: zinc[500],
  letterSpacing: "0.04em",
  textTransform: "uppercase",
} as const;

/** One column in the lists: a drag handle, a show/hide box, the label, and a pin toggle. */
function SortableColumnRow({
  row,
  onTogglePin,
  onToggleVisibility,
}: {
  row: ColumnRow;
  onTogglePin: () => void;
  onToggleVisibility: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id });
  return (
    <Box
      ref={(node: HTMLDivElement | null) => setNodeRef(node)}
      data-sb={TestIds.dispatch.columnMenuRow}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        bgcolor: isDragging ? zinc[700] : "transparent",
      }}
    >
      <Box
        component="span"
        aria-label={`Drag ${row.label}`}
        {...attributes}
        {...listeners}
        sx={{
          display: "inline-flex",
          cursor: "grab",
          color: zinc[500],
          touchAction: "none",
          "&:active": { cursor: "grabbing" },
          "&:hover": { color: "white" },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 16 }} />
      </Box>
      <Checkbox
        size="small"
        checked={row.visible}
        onChange={onToggleVisibility}
        sx={{ p: 0.25, color: zinc[400] }}
        inputProps={{ "aria-label": `Show ${row.label}` }}
      />
      <Typography
        sx={{
          flex: 1,
          fontSize: "0.8125rem",
          color: row.visible ? zinc[100] : zinc[400],
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {row.label}
        {!row.fromView && (
          <Box component="span" sx={{ ml: 0.75, fontSize: "0.6875rem", color: zinc[500] }}>
            added
          </Box>
        )}
      </Typography>
      <IconButton
        size="small"
        aria-label={row.pinned ? `Unpin ${row.label}` : `Pin ${row.label}`}
        onClick={onTogglePin}
        sx={{ color: row.pinned ? "white" : zinc[500], "&:hover": { color: "white" } }}
      >
        {row.pinned ? <PushPinIcon sx={{ fontSize: 16 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Box>
  );
}

/** A labelled row of the display options: the label on the left, the control filling the rest. */
function OptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5, py: 0.25 }}>
      <Typography sx={{ fontSize: "0.8125rem", color: zinc[300], width: 72, flex: "none" }}>{label}</Typography>
      {children}
    </Box>
  );
}

/**
 * The "Display" button in the header and the popover it opens, the one place a member shapes the table: the row
 * grain, grouping, sort, then a "Pinned" list and a "Columns" list dragged into order, every row shown or hidden by
 * its box and pinned or unpinned by its pin. Grouping and sort pick from every field, not only the columns showing:
 * the acknowledgement layer is never a column and is the first thing a desk sorts by, so those two are searched
 * pickers rather than selects. All of it is the table's own state, so header clicks and grabbers do the same things
 * another way. Hidden columns stay listed where they sit, so a field the view left out is one click from showing.
 * Reset drops the member's state on the view: layout, controls, sort, grouping, grain, and ad-hoc conditions.
 */
export default function DispatchDisplayMenu({
  table,
  labelFor,
  viewColumnIds,
  onColumnPinningChange,
  groupBy,
  groupOptions,
  onGroupByChange,
  sort,
  sortOptions,
  onSortChange,
  onReset,
  personalized,
  grain,
  viewGrain,
  onGrainChange,
}: {
  table: DataTableInstance<DispatchTableRow>;
  labelFor: (columnId: string) => string;
  /** The ids the active view defines; every other column is marked as one the member added. */
  viewColumnIds: ReadonlySet<string>;
  onColumnPinningChange: (left: string[]) => void;
  /** The column id of the grouping field, or `null` for none. */
  groupBy: string | null;
  groupOptions: readonly FieldOption[];
  onGroupByChange: (key: string | null) => void;
  sort: DisplaySort;
  sortOptions: readonly FieldOption[];
  onSortChange: (sort: DisplaySort) => void;
  onReset: () => void;
  /** Whether the member's state changes anything about the view: what Reset would drop. */
  personalized: boolean;
  /** The grain the rows are at: the member's over the view's. */
  grain: DispatchGrain;
  /**
   * The view definition's own grain; choosing it hands the grain back to the view rather than pinning it.
   *
   * Handing back clears the member's layer, so a workspace-level grain on the view would show through instead.
   * Nothing writes one today.
   */
  viewGrain: DispatchGrain;
  /** `null` hands the grain back to the view. */
  onGrainChange: (grain: DispatchGrain | null) => void;
}) {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // The table's own columns (the unread dot, the selection box) are not the member's to lay out.
  const pinnedColumns = table.getStartLeafColumns().filter((column) => !isStructuralColumnId(column.id));
  const centerColumns = table.getCenterLeafColumns();
  const pinnedIds = pinnedColumns.map((column) => column.id);

  const toRow = (column: Column<DataTableFeatures, DispatchTableRow>, pinned: boolean): ColumnRow => ({
    id: column.id,
    label: labelFor(column.id),
    visible: column.getIsVisible(),
    pinned,
    fromView: viewColumnIds.has(column.id),
  });
  const setPinned = (columnId: string, pin: boolean) =>
    onColumnPinningChange(pin ? [...pinnedIds, columnId] : pinnedIds.filter((id) => id !== columnId));
  const reorder = (ids: string[], apply: (next: string[]) => void) => (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = ids.indexOf(String(active.id));
    const to = ids.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    apply(arrayMove(ids, from, to));
  };
  const direction = sort?.direction ?? "desc";

  return (
    <>
      <Tooltip title="Display: grouping, sort, and columns">
        <ButtonBase
          ref={anchorRef}
          data-sb={TestIds.dispatch.displayMenu}
          aria-label="Display"
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
          sx={headerButtonSx(open)}
        >
          <TuneIcon sx={{ fontSize: 18 }} />
          Display
        </ButtonBase>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              width: 340,
              maxHeight: "75vh",
              overflowY: "auto",
              bgcolor: zinc[800],
              border: `1px solid ${zinc[600]}`,
              p: 1,
              mt: 0.5,
            },
          },
        }}
      >
        <OptionRow label="Rows">
          <Select
            data-sb={TestIds.dispatch.rowsKind}
            value={grain}
            onChange={(event) => {
              const value: DispatchGrain = event.target.value === "message" ? "message" : "driver";
              onGrainChange(value === viewGrain ? null : value);
            }}
            inputProps={{ "aria-label": "Rows" }}
            MenuProps={{ slotProps: menuSlotProps }}
            sx={selectSx}
          >
            <MenuItem value="driver" sx={{ fontSize: "0.8125rem", py: 0.25 }}>
              One per driver
            </MenuItem>
            <MenuItem value="message" sx={{ fontSize: "0.8125rem", py: 0.25 }}>
              One per message
            </MenuItem>
          </Select>
        </OptionRow>
        <OptionRow label="Group by">
          <DispatchFieldPicker
            testId={TestIds.dispatch.groupBy}
            ariaLabel="Group by"
            value={groupBy}
            valueLabel={groupBy ? labelFor(groupBy) : undefined}
            options={groupOptions}
            onChange={onGroupByChange}
          />
        </OptionRow>
        <OptionRow label="Sort by">
          <DispatchFieldPicker
            testId={TestIds.dispatch.sortBy}
            ariaLabel="Sort by"
            value={sort?.key ?? null}
            valueLabel={sort ? labelFor(sort.key) : undefined}
            options={sortOptions}
            onChange={(key) => onSortChange(key === null ? null : { key, direction })}
          />
          <Tooltip title={direction === "asc" ? "Ascending" : "Descending"}>
            <span>
              <IconButton
                size="small"
                aria-label={direction === "asc" ? "Sort descending" : "Sort ascending"}
                disabled={!sort}
                onClick={() => sort && onSortChange({ key: sort.key, direction: direction === "asc" ? "desc" : "asc" })}
                sx={{ color: zinc[300], bgcolor: zinc[700], borderRadius: 1, height: 30, width: 30 }}
              >
                {direction === "asc" ? (
                  <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </OptionRow>
        <Divider sx={{ my: 0.75, borderColor: zinc[700] }} />
        {pinnedColumns.length > 0 && (
          <>
            <Typography sx={sectionLabelSx}>Pinned</Typography>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={reorder(pinnedIds, onColumnPinningChange)}
            >
              <SortableContext items={pinnedIds} strategy={verticalListSortingStrategy}>
                {pinnedColumns.map((column) => (
                  <SortableColumnRow
                    key={column.id}
                    row={toRow(column, true)}
                    onTogglePin={() => setPinned(column.id, false)}
                    onToggleVisibility={() => column.toggleVisibility()}
                  />
                ))}
              </SortableContext>
            </DndContext>
            <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />
          </>
        )}
        <Typography sx={sectionLabelSx}>Columns</Typography>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={reorder(
            centerColumns.map((column) => column.id),
            (next) => table.setColumnOrder(next),
          )}
        >
          <SortableContext items={centerColumns.map((column) => column.id)} strategy={verticalListSortingStrategy}>
            {centerColumns.map((column) => (
              <SortableColumnRow
                key={column.id}
                row={toRow(column, false)}
                onTogglePin={() => setPinned(column.id, true)}
                onToggleVisibility={() => column.toggleVisibility()}
              />
            ))}
          </SortableContext>
        </DndContext>
        <Divider sx={{ my: 0.5, borderColor: zinc[700] }} />
        <Tooltip title={personalized ? "Drop every change you made to this view" : "Nothing changed from the view"}>
          <span>
            <ButtonBase
              data-sb={TestIds.dispatch.columnReset}
              aria-label="Reset view"
              disabled={!personalized}
              onClick={() => {
                onReset();
                setOpen(false);
              }}
              sx={{
                width: "100%",
                justifyContent: "center",
                px: 1,
                py: 0.75,
                borderRadius: 1,
                color: personalized ? zinc[200] : zinc[600],
                fontSize: "0.8125rem",
                "&:hover": { bgcolor: zinc[700], color: "white" },
              }}
            >
              Reset to the view's defaults
            </ButtonBase>
          </span>
        </Tooltip>
      </Popover>
    </>
  );
}
